import { type ReactNode, useEffect } from 'react';
import { getAuthAdapter } from '../auth/createAuthAdapter';
import { fetchMyPermissions } from '../auth/fetchMyPermissions';
import { rotateSessionId } from '../lib/correlation';
import { trackException } from '../lib/telemetry';
import { useAuthStore } from '../stores/authStore';

// Permissions fetch is best-effort across all auth entry points. A failure
// leaves rules null (default deny via <Can>) but never undoes the auth state
// — the user is already authenticated.
async function fetchRulesAndStore(): Promise<void> {
  try {
    const rules = await fetchMyPermissions();
    useAuthStore.getState()._setRules(rules);
  } catch (error) {
    console.error('Failed to fetch permissions:', error);
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  // One-time boot check: restore an existing session (cookie or Keychain).
  // Skipped on /auth/callback — auth state is set there directly via handleAuthCallback.
  useEffect(() => {
    if (window.location.pathname.startsWith('/auth/callback')) {
      useAuthStore.getState()._setLoading(false);
      return;
    }

    getAuthAdapter()
      .restoreSession()
      .then(async (session) => {
        const user = session?.user ?? null;
        useAuthStore.getState()._setUser(user);
        // Await inside .then() so .finally() (which clears isLoading) waits
        // until rules are resolved too. Prevents a flash of unpermissioned UI.
        if (user) await fetchRulesAndStore();
      })
      .catch((error: unknown) => {
        console.error('Failed to restore session:', error);
        if (error instanceof Error) trackException(error, { source: 'restoreSession' });
      })
      .finally(() => {
        useAuthStore.getState()._setLoading(false);
      });
  }, []);

  // Wire cross-tab logout for the lifetime of the app. Mobile deep-link auth
  // callbacks are handled by <DeepLinkAuthHandler>, which routes them to
  // /auth/callback — the shared handler web reaches via the backend HTTP redirect.
  useEffect(() => {
    const unsubCrossTab = getAuthAdapter().onCrossTabLogout(() => {
      useAuthStore.getState()._setUser(null);
      // Mirror the authStore.logout() flow: mint a fresh session ID so the
      // next user's telemetry doesn't inherit the previous session.
      rotateSessionId();
      useAuthStore.getState()._setRules(null);
      getAuthAdapter()
        .clearLocalSession()
        .catch((err: unknown) => {
          console.error('Cross-tab session clear failed:', err);
          if (err instanceof Error) trackException(err, { source: 'crossTabLogout' });
        });
    });

    return unsubCrossTab;
  }, []);

  return <>{children}</>;
}

// Public hook. AuthProvider must be mounted at app root; its useEffect hooks wire
// session restore and cross-tab logout. Mobile deep-link auth callbacks are handled
// separately by <DeepLinkAuthHandler>, which is mounted inside the router.
export function useAuth() {
  const user = useAuthStore((s) => s.user);
  const isLoading = useAuthStore((s) => s.isLoading);
  const initiateLogin = useAuthStore((s) => s.initiateLogin);
  const handleAuthCallback = useAuthStore((s) => s.handleAuthCallback);
  const logout = useAuthStore((s) => s.logout);
  const refresh = useAuthStore((s) => s.refresh);

  return {
    user,
    isLoading,
    isAuthenticated: !!user,
    isMember: user?.userType === 'member',
    isProvider: user?.userType === 'provider',
    isStaff: user?.userType === 'staff',
    initiateLogin,
    handleAuthCallback,
    logout,
    refresh,
  };
}
